import { Suspense, lazy, useMemo } from 'react'
import getMFEConfig from 'helpers/getMFEConfig'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import { useAtom, useAtomValue } from 'jotai'
import { modalStatusAtom, selectedAccountAtom } from 'store/atoms'
import { triggerAnalyticsEvent } from '@syf-utils/analytics'
import { useIVASetChatStatus } from 'iva-react-web-ui'
import { getBrandRefreshPhase } from 'helpers/getBrandRefreshPhase'
import { useCreditAccounts } from 'const/syfApiQueries'
import findAccount from 'helpers/findAccount'
import { ModalHeader } from 'syf-component-library/ui/patterns/SlideInModalPortal'
import i18n from 'strings/i18n'
import Loader from 'authorization-module/lib/ui/atoms/Loader'
import { SlideInModalPortalWrapper } from 'ui/patterns/SlideInModalPortalWrapper'

const CardStatusTrackerMFE = lazy(
  () => import('@card-actions/card-status-tracker')
)

const CardStatusTrackerModal = () => {
  const selectedAccount = useAtomValue(selectedAccountAtom)
  const [{ modalId, modalTriggerId }, updateModalStatus] =
    useAtom(modalStatusAtom)
  const isModalOpen = modalId === 'CardStatusTracker'

  const { data: { accounts = [] } = {} } = useCreditAccounts()

  const analyticsConfigV2 = useMemo(() => {
    const cipherKey = 'cipher.accountId'
    const account = findAccount(accounts, selectedAccount)
    return {
      accountId: account?.info?.[cipherKey] ?? null,
      accountIdType: account?.info?.accountIdType ?? null,
      correlationId: sessionStorage.getItem('correlationId'),
      apptimizeFlag: String(isFeatureFlagEnabled('isCardStatusTrackerEnabled'))
    }
  }, [selectedAccount, accounts])

  const handleCloseModal = () => {
    document.getElementById('card-status-tracker-link')?.focus()
    updateModalStatus('', '')
  }

  const { pointOfNeedChat } = useIVASetChatStatus()

  const handleLostOrStolenClick = () => {
    pointOfNeedChat('RLS', selectedAccount)
  }

  const handleIva = (pointOfNeed: 'CST' | 'AC') => {
    pointOfNeedChat(pointOfNeed, selectedAccount)
  }

  if (!isModalOpen) return null

  const mfeConfig = getMFEConfig('mfe')
  const title = i18n({ modal: 'cardStatusTrackerTitle' }) as string

  return (
    <SlideInModalPortalWrapper
      ariaLabel={title}
      containerId={modalTriggerId}
      escapeModalHandler={handleCloseModal}
      header={
        <ModalHeader
          closeModal={handleCloseModal}
          content={title}
          data-cy="card-status-tracker-drawer-name"
          dataReason="x button"
          dataType="card status tracker"
        />
      }
      id={`card-status-tracker-modal-${selectedAccount}`}
      isOpen={isModalOpen}
      modalTriggerId={modalTriggerId}
    >
      <Suspense fallback={<Loader />}>
        <CardStatusTrackerMFE
          analyticsConfigV2={analyticsConfigV2}
          onCloseModal={handleCloseModal}
          featureFlags={{ brandRefreshPhase: getBrandRefreshPhase() }}
          handleIva={handleIva}
          lostOrStolenMfeHandler={handleLostOrStolenClick}
          mfeConfig={mfeConfig}
          selectedAccountId={selectedAccount}
          triggerAnalyticsEvent={triggerAnalyticsEvent}
        />
      </Suspense>
    </SlideInModalPortalWrapper>
  )
}

export default CardStatusTrackerModal
